import { useMemo } from "react";
import { useIntl } from "react-intl";
import { Disclosure } from "@/components/design/Disclosure";
import type { MatchCondition, MatchConditionTypename } from "../design-codec";
import type { MatchData } from "../match-design-types";
import { DetailsSection } from "./DetailsSection";
import { ExpertiseSection } from "./ExpertiseSection";
import { FreitextSection } from "./FreitextSection";

const DETAILS_TYPES: ReadonlySet<MatchConditionTypename> = new Set([
  "GenderCondition",
  "MaritalCondition",
  "CountryCondition",
  "CitizenshipCondition",
  "JobDepartmentCondition",
  "JobLocationCondition",
  "LanguageCondition",
  "AgeCondition",
  "AnnualSalaryCondition",
  "ChildrenCountCondition",
  "CityCondition",
  "NationalityCondition",
  "DriversLicenseCondition",
]);

export interface RequirementsPanelProps {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  readOnly?: boolean;
}

export function RequirementsPanel({ data, update, readOnly = false }: RequirementsPanelProps) {
  const intl = useIntl();
  // ExpertiseCondition wird nicht mehr eingesammelt: ExpertiseSection liest/schreibt sie
  // direkt aus data.conditions (wie im Rank-Pfad), nicht ueber replaceSection.
  const { details, experience, other } = useMemo(() => {
    const details: MatchCondition[] = [];
    const experience: MatchCondition[] = [];
    const other: MatchCondition[] = [];
    for (const c of data.conditions) {
      if (DETAILS_TYPES.has(c.__typename)) details.push(c);
      else if (c.__typename === "ExpertiseCondition") continue;
      else if (c.__typename === "ExperienceCondition") experience.push(c);
      else other.push(c);
    }
    return { details, experience, other };
  }, [data.conditions]);

  function replaceSection(section: MatchCondition[], updated: MatchCondition[]) {
    const sectionSet = new Set(section);
    const rebuilt: MatchCondition[] = [];
    for (const c of data.conditions) {
      if (sectionSet.has(c)) continue;
      rebuilt.push(c);
    }
    rebuilt.push(...updated);
    update({ conditions: rebuilt });
  }

  return (
    <div className="space-y-3">
      <Disclosure
        title={intl.formatMessage({ id: "match.teamReqsSection", defaultMessage: "Teamanforderungen" })}
        defaultOpen
      >
        <div className="space-y-6">
          {/* Fachwissen zuoberst, spiegelt die Rank-Reihenfolge (ChallengeStep). */}
          <ExpertiseSection data={data} update={update} readOnly={readOnly} personCount />
          <FreitextSection
            conditions={experience as Extract<MatchCondition, { __typename: "ExperienceCondition" }>[]}
            onChange={(next) => replaceSection(experience, next)}
            readOnly={readOnly}
            showRequirement
          />
          <DetailsSection
            conditions={details}
            onChange={(next) => replaceSection(details, next)}
            readOnly={readOnly}
            showRequirement
          />
        </div>
      </Disclosure>


      {other.length > 0 ? (
        <div className="text-[11px] text-ink/45">
          {intl.formatMessage(
            {
              id: "match.requirements.otherUnchanged",
              defaultMessage: "{count, plural, one {# weitere Anforderung bleibt} other {# weitere Anforderungen bleiben}} unverändert erhalten.",
            },
            { count: other.length },
          )}
        </div>
      ) : null}
    </div>
  );
}
